/** @format */

import React from "react";
import { Button, Dropdown, Menu, Spin, Table } from "antd";
import Column from "antd/lib/table/Column";
import { format } from "date-fns";
import { Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { IApiRequestsDataPerAccount } from "../Charts.types";
import { useQueryAccounts } from "../../../../apis/swr/fetchers";
import { DownloadOutlined } from "@ant-design/icons";

interface IAsrChartsViewProps {
  chartData: any;
  chartOptions: any;
  data: IApiRequestsDataPerAccount[];
  downloadChart: (format: string) => void;
}

const AsrChartsView: React.FC<IAsrChartsViewProps> = ({ chartData, chartOptions, data, downloadChart }) => {
  const { accounts, isLoading } = useQueryAccounts();
  const findWebname = (id: string) => {
    const found = id
      ? accounts && accounts.find((account) => account.accountId === id)
      : null;
    if (found) return found.webName;
    else return null;
  };
  const calculateDateTitle = (daysAgo: number) => {
    let d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return format(d, "MMM-do");
  };
  const sorterFunc = (
    a: IApiRequestsDataPerAccount,
    b: IApiRequestsDataPerAccount,
    daysBack: number
  ) => {
    let aValue = a && a.values ? a.values[a.values.length - daysBack] : [0, 0];
    let bValue = b && b.values ? b.values[b.values.length - daysBack] : [0, 0];
    return aValue[1] - bValue[1];
  };

    const menu = (
      <Menu>
        <Menu.Item key="png">
          <Button
            size="small"
            onClick={() => downloadChart('png')}
            style={{ width: '100%' }}
          >
            Download PNG
          </Button>
        </Menu.Item>
        <Menu.Item key="jpg">
          <Button
            size="small"
            onClick={() => downloadChart('jpg')}
            style={{ width: '100%' }}
          >
            Download JPG
          </Button>
        </Menu.Item>
        <Menu.Item key="pdf">
          <Button
            size="small"
            onClick={() => downloadChart('pdf')}
            style={{ width: '100%' }}
          >
            Download PDF
          </Button>
        </Menu.Item>
      </Menu>
    );

  return (
    <Spin spinning={isLoading}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '2em 2em 0 2em', marginBottom: '-2em', position: 'relative', zIndex: 1 }}>
        <Dropdown overlay={menu} trigger={['click']}>
          <Button icon={<DownloadOutlined />} size="small">
            Download
          </Button>
        </Dropdown>
      </div>
      <div style={{ padding: "2rem", paddingTop: 0, width: "100%" }}>
        <div id="chart-canvas" style={{ position: 'relative', height: '400px' }}>
            <Line data={chartData} options={chartOptions} />
        </div>
        {/* <div style={{ padding: "1rem" }}>
          <Button onClick={() => downloadChart('png')}>Download PNG</Button>
          <Button onClick={() => downloadChart('jpg')}>Download JPG</Button>
          <Button onClick={() => downloadChart('pdf')}>Download PDF</Button>
        </div> */}
        <div style={{ padding: "1rem" }}>
          <Table
            dataSource={data}
            rowKey="accountId"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "50", "100", "250"],
            }}
          >
            <Column
              title="Account ID"
              dataIndex="accountId"
              key="accountId"
              render={(id) => (id ? id : "No ID")}
              sorter={(a: { accountId: string }, b: { accountId: string }) => {
                a.accountId = a.accountId ? a.accountId : "No ID";
                b.accountId = b.accountId ? b.accountId : "No ID";
                return a.accountId.localeCompare(b.accountId);
              }}
            />
            <Column
              title="Name"
              dataIndex="accountId"
              key="name"
              render={(id) => {
                return id ? findWebname(id) : null;
              }}
              sorter={(a: { accountId: string }, b: { accountId: string }) => {
                let aWebName = findWebname(a.accountId)
                  ? (findWebname(a.accountId) as string)
                  : "";
                let bWebName = findWebname(b.accountId)
                  ? (findWebname(b.accountId) as string)
                  : "";
                return aWebName.localeCompare(bWebName);
              }}
            />
            <Column
              title={calculateDateTitle(7)}
              dataIndex="values"
              key="day-7"
              align="center"
              render={(values) => {
                const last = values ? values.length : 0;
                return values ? values[last - 7][1] : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                return sorterFunc(a, b, 7);
              }}
            />
            <Column
              title={calculateDateTitle(6)}
              dataIndex="values"
              key="day-6"
              align="center"
              render={(values) => {
                const last = values ? values.length : 0;
                return values ? values[last - 6][1] : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                return sorterFunc(a, b, 6);
              }}
            />
            <Column
              title={calculateDateTitle(5)}
              dataIndex="values"
              key="day-5"
              align="center"
              render={(values) => {
                const last = values ? values.length : 0;
                return values ? values[last - 5][1] : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                return sorterFunc(a, b, 5);
              }}
            />
            <Column
              title={calculateDateTitle(4)}
              dataIndex="values"
              key="day-4"
              align="center"
              render={(values) => {
                const last = values ? values.length : 0;
                return values ? values[last - 4][1] : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                return sorterFunc(a, b, 4);
              }}
            />
            <Column
              title={calculateDateTitle(3)}
              dataIndex="values"
              key="day-3"
              align="center"
              render={(values) => {
                const last = values ? values.length : 0;
                return values ? values[last - 3][1] : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                return sorterFunc(a, b, 3);
              }}
            />
            <Column
              title={calculateDateTitle(2)}
              dataIndex="values"
              key="day-2"
              align="center"
              render={(values) => {
                const last = values ? values.length : 0;
                return values ? values[last - 2][1] : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                return sorterFunc(a, b, 2);
              }}
            />
            <Column
              title={calculateDateTitle(1)}
              dataIndex="values"
              key="day-1"
              align="center"
              render={(values) => {
                const last = values ? values.length : 0;
                return values ? values[last - 1][1] : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                return sorterFunc(a, b, 1);
              }}
            />
            <Column
              title="Total"
              dataIndex="values"
              key="total"
              align="center"
              render={(values) => {
                //Return total of values from last 7 days
                const slicedValues = values ? values.slice(-7) : null;
                return slicedValues
                  ? slicedValues.reduce((acc, curr) => acc + curr[1], 0)
                  : null;
              }}
              sorter={(a: IApiRequestsDataPerAccount, b) => {
                const aVal = a.values
                  ? a.values.slice(-7).reduce((acc, curr) => acc + curr[1], 0)
                  : 0;
                const bVal = b.values
                  ? b.values.slice(-7).reduce((acc, curr) => acc + curr[1], 0)
                  : 0;
                return aVal - bVal;
              }}
            />
            <Column
              title="Details"
              dataIndex="accountId"
              key="details"
              render={(id) => {
                const findWebname = () => {
                  const found = id
                    ? accounts &&
                      accounts.find((account) => account.accountId === id)
                    : null;
                  if (found) return found.webName;
                  else return null;
                };
                if (id)
                  return (
                    <Link
                      to={`/account/${id ? id : ""}`}
                      target="_blank"
                    >
                      <Button size="small">View</Button>
                    </Link>
                  );
                else
                  return (
                    <Button size="small" disabled={true}>
                      View
                    </Button>
                  );
              }}
            />
          </Table>
        </div>
      </div>
    </Spin>
  );
};

export default AsrChartsView;

